import { Link, useLocation } from "react-router-dom";

/**
 * Sigillo flottante «YOUR AI CONCIERGE» — rosa dei venti dorata su medaglione
 * di marmo nero. SVG inline: nitido, leggero, responsive. L'apertura punta alla
 * pagina /concierge esistente; nessuna seconda chat viene creata.
 */
const CompassRose = () => (
    <svg viewBox="0 0 64 64" className="h-full w-full" aria-hidden="true">
        <defs>
            <linearGradient id="cr-gold" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#f9e7a8" />
                <stop offset="0.45" stopColor="#d9b451" />
                <stop offset="1" stopColor="#8a6a1f" />
            </linearGradient>
            <linearGradient id="cr-gold-dark" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#c69b3c" />
                <stop offset="1" stopColor="#6e5318" />
            </linearGradient>
            <radialGradient id="cr-center" cx="0.5" cy="0.42" r="0.65">
                <stop offset="0" stopColor="#fff3c4" />
                <stop offset="0.55" stopColor="#e2c063" />
                <stop offset="1" stopColor="#7c5f1e" />
            </radialGradient>
            <radialGradient id="cr-marble" cx="0.35" cy="0.3" r="0.9">
                <stop offset="0" stopColor="#1c1c1f" />
                <stop offset="0.6" stopColor="#0d0d0f" />
                <stop offset="1" stopColor="#050506" />
            </radialGradient>
        </defs>
        {/* fondo marmo nero */}
        <circle cx="32" cy="32" r="29" fill="url(#cr-marble)" />
        <circle cx="32" cy="32" r="29" fill="none" stroke="url(#cr-gold)" strokeWidth="2.4" />
        <circle cx="32" cy="32" r="24.5" fill="none" stroke="#d9b451" strokeOpacity="0.45" strokeWidth="0.8" />
        {/* cardinali */}
        <text x="32" y="11.5" textAnchor="middle" fontSize="6.5" fill="#e8cd7d" fontFamily="Georgia, serif" fontWeight="bold">N</text>
        <text x="32" y="58.5" textAnchor="middle" fontSize="6.5" fill="#e8cd7d" fontFamily="Georgia, serif" fontWeight="bold">S</text>
        <text x="56.5" y="34.5" textAnchor="middle" fontSize="6.5" fill="#e8cd7d" fontFamily="Georgia, serif" fontWeight="bold">E</text>
        <text x="7.5" y="34.5" textAnchor="middle" fontSize="6.5" fill="#e8cd7d" fontFamily="Georgia, serif" fontWeight="bold">W</text>
        {/* punte principali: meta' chiara + meta' scura per l'effetto 3D */}
        {[[0, -17], [17, 0], [0, 17], [-17, 0]].map(([dx, dy], i) => {
            const cx = 32 + dx, cy = 32 + dy;
            const px = dy !== 0 ? 3.4 : 0, py = dx !== 0 ? 3.4 : 0;
            return (
                <g key={i}>
                    <polygon points={`32,32 ${32 + px},${32 + py} ${cx},${cy}`} fill="url(#cr-gold)" />
                    <polygon points={`32,32 ${32 - px},${32 - py} ${cx},${cy}`} fill="url(#cr-gold-dark)" />
                </g>
            );
        })}
        {/* punte diagonali piu' corte */}
        {[[12, -12], [12, 12], [-12, 12], [-12, -12]].map(([dx, dy], i) => {
            const cx = 32 + dx, cy = 32 + dy;
            return (
                <g key={`d${i}`}>
                    <polygon points={`32,32 ${32 + (dy > 0 ? 2.2 : -2.2)},${32 + (dx > 0 ? 2.2 : -2.2)} ${cx},${cy}`} fill="url(#cr-gold)" opacity="0.92" />
                    <polygon points={`32,32 ${32 - (dy > 0 ? 2.2 : -2.2)},${32 - (dx > 0 ? 2.2 : -2.2)} ${cx},${cy}`} fill="url(#cr-gold-dark)" opacity="0.92" />
                </g>
            );
        })}
        {/* centro luminoso */}
        <circle cx="32" cy="32" r="5.2" fill="url(#cr-center)" stroke="#f9e7a8" strokeWidth="0.7" />
        <circle cx="32" cy="32" r="1.6" fill="#0d0d0f" />
    </svg>
);

export const ConciergeFab = () => {
    const { pathname } = useLocation();
    if (pathname.startsWith("/concierge")) return null;
    return (
        <Link
            to="/concierge"
            data-testid="concierge-fab"
            aria-label="Apri il Concierge AI di Tourvoyagers"
            className="group fixed bottom-6 right-5 sm:right-6 z-40 flex flex-col items-center"
        >
            {/* medaglione con rosa dei venti */}
            <span className="relative block h-14 w-14 sm:h-16 sm:w-16 rounded-full transition-all duration-300 group-hover:scale-[1.06] group-hover:brightness-110 group-active:scale-95 shadow-[0_10px_28px_rgba(0,0,0,0.6),0_0_18px_rgba(217,180,81,0.18)] group-hover:shadow-[0_12px_34px_rgba(0,0,0,0.65),0_0_30px_rgba(217,180,81,0.4)]">
                <CompassRose />
                {/* riflesso dorato lento sul bordo — disattivato se l'utente riduce il movimento */}
                <span aria-hidden="true" className="concierge-sheen pointer-events-none absolute inset-0 rounded-full" />
            </span>
            {/* targhetta: nascosta su smartphone */}
            <span className="mt-1.5 hidden sm:flex items-center gap-1.5 rounded-full border border-amber-500/50 bg-[#0b0b0c]/92 px-3.5 py-1.5 shadow-[0_6px_20px_rgba(0,0,0,0.5)] backdrop-blur-md transition-colors group-hover:border-amber-300/80">
                <span className="text-amber-400 text-[9px]">✦</span>
                <span className="font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-100">
                    Your AI Concierge
                </span>
            </span>
        </Link>
    );
};
